import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { trpc } from '../../trpc';
import { analytics } from '../../lib/analytics';
import { Button } from '../../components/ui/button';
import { LoaderCircle, ArrowLeft } from 'lucide-react';
import { Card } from '../../components/ui/card';
import {
  ResumeMatchScore,
  EvaluationOverview,
  StrengthsList,
  ImprovementsList,
} from '../../components/evaluation';
import { useTranslation } from 'react-i18next';

// Simple route without any authentication checks that might redirect
export const Route = createFileRoute('/evaluation/$evaluationId')({
  component: EvaluationPage,
});

function EvaluationPage() {
  const { t } = useTranslation();
  const { evaluationId } = Route.useParams();

  // Parse the evaluationId to an integer
  const evaluationIdNum = parseInt(evaluationId, 10);

  // Fetch the evaluation data without auth checks that might cause redirects
  const evaluationQuery = trpc.resumeEvaluation.getById.useQuery(
    { evaluationId: evaluationIdNum },
    {
      enabled: !isNaN(evaluationIdNum),
      refetchOnWindowFocus: false,
      onSuccess: (data: any) => {
        console.log('Successfully fetched evaluation data:', data);
      },
      onError: (error: any) => {
        console.error('Error fetching evaluation:', error);
      },
    }
  );

  // Resume Value funnel: record that the user consumed (viewed) the generated evaluation.
  // Keyed on the evaluation id so it fires once per distinct evaluation, not on every refetch.
  useEffect(() => {
    if (evaluationQuery.data) {
      analytics.track('resume_evaluation_viewed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluationQuery.data?.id]);

  // Handle loading state
  if (evaluationQuery.isLoading) {
    return (
      <div className='max-w-6xl mx-auto py-8 px-4'>
        <div className='py-12 flex flex-col items-center justify-center'>
          <LoaderCircle className='h-8 w-8 animate-spin text-gray-400 mb-4' />
          <p className='text-gray-500'>{t('evaluation.loading')}</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (evaluationQuery.error) {
    return (
      <div className='max-w-6xl mx-auto py-8 px-4'>
        <div className='py-12 flex flex-col items-center justify-center'>
          <p className='text-red-500 mb-2'>{t('evaluation.error')}</p>
          <p className='text-gray-500 text-sm'>
            {evaluationQuery.error.message}
          </p>
          <Button
            variant='outline'
            className='mt-4'
            onClick={() => evaluationQuery.refetch()}
          >
            {t('evaluation.tryAgain')}
          </Button>
        </div>
      </div>
    );
  }

  // Handle no data state
  if (!evaluationQuery.data) {
    return (
      <div className='max-w-6xl mx-auto py-8 px-4'>
        <div className='py-12 flex flex-col items-center justify-center'>
          <p className='text-gray-500 mb-4'>{t('evaluation.noData')}</p>
          <Button
            onClick={() => window.history.back()}
            className='flex items-center gap-2'
          >
            <ArrowLeft className='h-4 w-4' />
            {t('evaluation.goBack')}
          </Button>
        </div>
      </div>
    );
  }

  // When we have data
  const evaluation = evaluationQuery.data;

  return (
    <div className='max-w-6xl mx-auto py-8 px-4'>
      <div className='flex justify-between items-start mb-8'>
        <div>
          <h1 className='text-2xl font-bold mb-2'>
            {t('evaluation.pageTitle')}
          </h1>
          <p className='text-gray-600'>
            {evaluation.application.position} {t('evaluation.forPosition')}{' '}
            {evaluation.application.company}
          </p>
        </div>

        {!evaluation.isCurrentResume && (
          <Button
            variant='outline'
            onClick={() => {
              // Open re-evaluation in a new tab
              window.open(
                `/evaluation?applicationId=${evaluation.application.id}`,
                '_blank'
              );
            }}
          >
            {t('evaluation.actions.reevaluate')}
          </Button>
        )}
      </div>

      {/* Header with Resume Match Score and Overview */}
      <div className='bg-white rounded-lg shadow-sm border p-6 mb-6'>
        <div className='flex flex-col md:flex-row'>
          {/* Score section */}
          <div className='md:w-1/3 md:pr-6 mb-6 md:mb-0'>
            <ResumeMatchScore
              score={evaluation.score}
              isCurrentResume={evaluation.isCurrentResume}
              createdAt={evaluation.createdAt}
            />
          </div>

          {/* Overview section */}
          <div className='md:w-2/3 md:border-l md:pl-6'>
            <EvaluationOverview evaluationText={evaluation.evaluationText} />
          </div>
        </div>
      </div>

      {/* Two column layout */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Strengths Column */}
        <Card className='p-6 bg-white shadow-sm'>
          <StrengthsList strengths={evaluation.strengths} />
        </Card>

        {/* Improvements Column */}
        <Card className='p-6 bg-white shadow-sm'>
          <ImprovementsList
            weaknesses={evaluation.weaknesses}
            improvements={evaluation.improvements}
          />
        </Card>
      </div>
    </div>
  );
}
